import React from 'react';
import { MenuSection, MenuSectionHeading } from '@atlaskit/side-nav-items/menu-section';
import { MenuList } from '@atlaskit/side-nav-items/menu-list';
import PagesIcon from '@atlaskit/icon/core/pages';
import { defineMessage, useIntl } from '@atlassian/jira-intl';
import { Nav4MenuLinkItem } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/nav4-menu-link-item/index.tsx';
import FavoriteButtonStateless from '@jira/platform__favouriting-favourite-button-stateless/src/view/view.tsx';
import { ENTITY_ID } from '@jira/navigation-apps__sidebar-nav4-context/src/common/constants.tsx';
import { useFormattedMessageAndIcon } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/use-formatted-message-and-icon/index.tsx';
import { OpsgenieSavedSearchAri } from '@atlassian/ari/opsgenie/saved-search';
import { useStarredSearchService } from '../../../../service/use-starred-search/useStarredSearchService.tsx';
import type { AlertsMenuItemContentNew$data } from '../__generated__/AlertsMenuItemContentNew.graphql.ts';

const starredSectionMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-operations.starred',
	// eslint-disable-next-line @atlassian/tangerine/i18n/messages-are-spelled-correctly
	defaultMessage: 'Starred',
	description:
		'Menu item label displayed in the operations sidebar. When clicked, shows only alerts that the user has marked as favorites.',
});

const alertsMessageDefinition = defineMessage({
	id: 'navigation-apps-sidebar-nav4-operations.alerts',
	// eslint-disable-next-line @atlassian/tangerine/i18n/messages-are-spelled-correctly
	defaultMessage: 'Alerts',
	description: 'Assets dropdown item to link customer to Operations Alerts page.',
});

interface StarSectionProps {
	starred: NonNullable<
		NonNullable<AlertsMenuItemContentNew$data>['opsgenie']
	>['savedSearches']['starred'];
	viewType: string | undefined;
	savedSearchIdFromURL?: string;
	setSelectedFilter: (filter: string) => void;
	setLastVisitedSearch: (search: string) => void;
}

export const StarSection = ({
	starred,
	viewType,
	savedSearchIdFromURL,
	setSelectedFilter,
	setLastVisitedSearch,
}: StarSectionProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const { unstarSavedSearch } = useStarredSearchService();
	const { icon: pagesIcon } = useFormattedMessageAndIcon(alertsMessageDefinition, PagesIcon);

	if (!starred || starred.length === 0) {
		return null;
	}

	return (
		<MenuSection isMenuListItem>
			<MenuSectionHeading>{formatMessage(starredSectionMessage)}</MenuSectionHeading>
			<MenuList>
				{starred.map((item) => {
					const savedSearchId = OpsgenieSavedSearchAri.parse(item.id).savedSearchId;
					const href = `/jira/ops/alerts?view=${viewType ?? 'list'}&query=${item.alertSearchQuery}&filter=${savedSearchId}`;

					return (
						<Nav4MenuLinkItem
							key={item.id}
							href={`/jira/ops/alerts?view=${viewType ?? 'list'}&query=${item.alertSearchQuery}&filter=${savedSearchId}`}
							elemBefore={pagesIcon}
							menuId={ENTITY_ID.ALARM_QUEUE(
								savedSearchIdFromURL ? savedSearchId : item.alertSearchQuery,
							)}
							onClick={() => {
								setSelectedFilter(item.id);
								setLastVisitedSearch(href);
							}}
							actionsOnHover={
								<FavoriteButtonStateless
									isFavorite
									isSmall
									pending={false}
									onClick={() => unstarSavedSearch(item)}
								/>
							}
						>
							{item.name}
						</Nav4MenuLinkItem>
					);
				})}
			</MenuList>
		</MenuSection>
	);
};
